import { UseTicketListOptions } from "@jugl-web/domain-resources/tickets/hooks/useTicketList/types";
import { TicketMutationEvent } from "@jugl-web/domain-resources/tickets/types/TicketMutationEvent";
import { doesTicketMatchListCriteria } from "@jugl-web/domain-resources/tickets/utils/doesTicketMatchListCriteria";
import { TicketDetailsModel } from "@jugl-web/rest-api/tickets/models";
import { TicketListItemModel } from "@jugl-web/rest-api/tickets/models/TicketListItem";
import { WithOptimistic } from "@jugl-web/utils";
import { Dispatch, SetStateAction, useCallback, useEffect } from "react";
import { useClientSupportPageContext } from "../providers/ClientSupportPageProvider/useClientSupportPageContext";

type UseSubscribeToTicketsMutationsOptions = Pick<
  UseTicketListOptions,
  "filter" | "meId" | "contactId" | "searchQuery" | "sort"
> & {
  hasMore: boolean;
  setTickets: Dispatch<SetStateAction<WithOptimistic<TicketListItemModel>[]>>;
  setTotalCount: Dispatch<SetStateAction<number>>;
};

/**
 * Subscribes to client-support ticket mutations and syncs the local list and total count via
 * `setTickets`/`setTotalCount` from `useTicketList`.
 */
export const useSubscribeToTicketsMutations = ({
  sort = "desc",
  hasMore,
  filter,
  meId,
  contactId,
  searchQuery,
  setTickets,
  setTotalCount,
}: UseSubscribeToTicketsMutationsOptions) => {
  const { ticketMutations$ } = useClientSupportPageContext();

  const applyTicketPatch = useCallback(
    (ticket: TicketDetailsModel) => {
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticket.id ? { ...ticket, _optimistic: false } : t
        )
      );
    },
    [setTickets]
  );

  const removeTicket = useCallback(
    (ticketId: string) => {
      setTickets((prev) => prev.filter((t) => t.id !== ticketId));
      setTotalCount((count) => Math.max(0, count - 1));
    },
    [setTickets, setTotalCount]
  );

  const addCreatedTicket = useCallback(
    (ticket: TicketDetailsModel) => {
      if (sort === "desc") {
        setTickets((prev) => [{ ...ticket, _optimistic: false }, ...prev]);
      } else if (!hasMore) {
        setTickets((prev) => [...prev, { ...ticket, _optimistic: false }]);
      }

      setTotalCount((count) => count + 1);
    },
    [hasMore, setTickets, setTotalCount, sort]
  );

  const handleTicketMutation = useCallback(
    (event: TicketMutationEvent) => {
      if (event.type === "deleted") {
        removeTicket(event.ticketId);
        return;
      }

      const ticketMatchesList = doesTicketMatchListCriteria(event.ticket, {
        filter,
        meId,
        contactId,
        searchQuery,
      });

      if (event.type === "created") {
        if (ticketMatchesList) {
          addCreatedTicket(event.ticket);
        }

        return;
      }

      if (ticketMatchesList) {
        applyTicketPatch(event.ticket);
        return;
      }

      removeTicket(event.ticket.id);
    },
    [
      applyTicketPatch,
      removeTicket,
      addCreatedTicket,
      filter,
      meId,
      contactId,
      searchQuery,
    ]
  );

  useEffect(() => {
    const subscription = ticketMutations$.subscribe(handleTicketMutation);

    return () => {
      subscription.unsubscribe();
    };
  }, [ticketMutations$, handleTicketMutation]);
};
